import * as R from 'ramda'
import React, { useState } from 'react'
import styled from 'styled-components'
import { Form, Checkbox } from 'semantic-ui-react'
import { Table as UnstyledTable } from 'antd'

type SentimentToggleSettings = {
  is_positive_feedback_with_comment_enabled: boolean
  is_positive_feedback_without_comment_enabled: boolean
  is_neutral_feedback_with_comment_enabled: boolean
  is_neutral_feedback_without_comment_enabled: boolean
  is_negative_feedback_with_comment_enabled: boolean
  is_negative_feedback_without_comment_enabled: boolean
}

type SurveySettings = {
  id: number
  name: string
  token: string
  settings: SentimentToggleSettings[]
}

type SurveySentimentToggleTemplateProps = {
  isAuthenticated: boolean
  isIntegrationActive: boolean
  onChange: Function
  onIsEverySurveyIncludedChange: Function
  isEverySurveyIncluded: boolean
  surveySettings: SurveySettings[]
  everyResponseOptionLabel: string
}

export default function SurveySentimentToggleTemplate({
  isIntegrationActive,
  isAuthenticated,
  onChange,
  onIsEverySurveyIncludedChange,
  isEverySurveyIncluded,
  surveySettings,
  everyResponseOptionLabel,
}: SurveySentimentToggleTemplateProps) {
  const [isEverySurveyIncludedState, setIsEverySurveyIncludedState] =
    useState<boolean>(isEverySurveyIncluded)
  const [surveySettingsState, setSurveySettingsState] = useState<SurveySettings[]>(surveySettings)

  function handleCheckboxChange(_, { value }) {
    setIsEverySurveyIncludedState(value)
    onIsEverySurveyIncludedChange(value)
  }

  const isCustomFilterSurveys = !isEverySurveyIncludedState
  const disabled = !isAuthenticated || !isIntegrationActive

  return (
    <>
      <Form className="mt-4">
        <Form.Group grouped>
          <label>When should this integration run?</label>
          <Form.Field>
            <Checkbox
              radio
              disabled={disabled}
              label={everyResponseOptionLabel}
              name="is_every_survey_included"
              value={true}
              checked={isEverySurveyIncludedState === true}
              onChange={handleCheckboxChange}
            />
          </Form.Field>
          <Form.Field>
            <Checkbox
              radio
              disabled={disabled}
              label="Let me choose"
              name="is_every_survey_included"
              value={false}
              checked={isEverySurveyIncludedState === false}
              onChange={handleCheckboxChange}
            />
          </Form.Field>
        </Form.Group>
      </Form>
      <SurveySentimentToggleSettings
        isIntegrationActive={isIntegrationActive}
        isAuthenticated={isAuthenticated}
        surveySettings={surveySettingsState}
        setSurveySettings={setSurveySettingsState}
        onChange={onChange}
        isCustomFilterSurveys={isCustomFilterSurveys}
      />
    </>
  )
}

type SurveySentimentToggleSettingsProps = {
  isAuthenticated: boolean
  isIntegrationActive: boolean
  surveySettings: SurveySettings[]
  setSurveySettings: Function
  onChange: Function
  isCustomFilterSurveys: boolean
}

function SurveySentimentToggleSettings({
  isIntegrationActive,
  isAuthenticated,
  surveySettings,
  setSurveySettings,
  onChange,
  isCustomFilterSurveys,
}: SurveySentimentToggleSettingsProps) {
  function handleOptionChange(surveyIndex, name, checked) {
    setSurveySettings((surveySettings: SurveySettings[]) => {
      const surveySetting = surveySettings[surveyIndex]
      onChange({ surveyToken: surveySetting.token, name, value: checked })
      return R.assocPath([surveyIndex, 'settings', name], checked, surveySettings)
    })
  }

  const disabled = !isAuthenticated || !isIntegrationActive

  function renderCell(value, { index, surveyId }, fieldName) {
    return (
      <Checkbox
        checked={value}
        disabled={disabled}
        toggle
        name={surveyId}
        onChange={(_, { checked }) => handleOptionChange(index, fieldName, checked)}
      />
    )
  }

  const columns = [
    {
      title: null,
      dataIndex: 'surveyName',
      key: 'surveyName',
      fixed: 'left',
    },
    {
      key: 'is_positive_feedback_with_comment_enabled',
      dataIndex: 'is_positive_feedback_with_comment_enabled',
      align: 'center',
      title: <SentimentColumnTitle sentiment="positive" hasComment />,
      render: (value, dataSourceItem) =>
        renderCell(value, dataSourceItem, 'is_positive_feedback_with_comment_enabled'),
    },
    {
      align: 'center',
      title: <SentimentColumnTitle sentiment="positive" hasComment={false} />,
      dataIndex: 'is_positive_feedback_without_comment_enabled',
      key: 'is_positive_feedback_without_comment_enabled',
      render: (value, dataSourceItem) =>
        renderCell(value, dataSourceItem, 'is_positive_feedback_without_comment_enabled'),
    },
    {
      align: 'center',
      title: <SentimentColumnTitle sentiment="neutral" hasComment />,
      dataIndex: 'is_neutral_feedback_with_comment_enabled',
      key: 'is_neutral_feedback_with_comment_enabled',
      render: (value, dataSourceItem) =>
        renderCell(value, dataSourceItem, 'is_neutral_feedback_with_comment_enabled'),
    },
    {
      align: 'center',
      title: <SentimentColumnTitle sentiment="neutral" hasComment={false} />,
      dataIndex: 'is_neutral_feedback_without_comment_enabled',
      key: 'is_neutral_feedback_without_comment_enabled',
      render: (value, dataSourceItem) =>
        renderCell(value, dataSourceItem, 'is_neutral_feedback_without_comment_enabled'),
    },
    {
      align: 'center',
      title: <SentimentColumnTitle sentiment="negative" hasComment />,
      dataIndex: 'is_negative_feedback_with_comment_enabled',
      key: 'is_negative_feedback_with_comment_enabled',
      render: (value, dataSourceItem) =>
        renderCell(value, dataSourceItem, 'is_negative_feedback_with_comment_enabled'),
    },
    {
      align: 'center',
      title: <SentimentColumnTitle sentiment="negative" hasComment={false} />,
      dataIndex: 'is_negative_feedback_without_comment_enabled',
      key: 'is_negative_feedback_without_comment_enabled',
      render: (value, dataSourceItem) =>
        renderCell(value, dataSourceItem, 'is_negative_feedback_without_comment_enabled'),
    },
  ]

  function mapSettingToDataSource(surveySettings) {
    return surveySettings.map((surveySetting, index) => ({
      surveyId: surveySetting.id,
      surveyName: surveySetting.name,
      surveyToken: surveySetting.token,
      index,
      ...surveySetting.settings,
    }))
  }

  const dataSource = mapSettingToDataSource(surveySettings)

  return (
    <div style={{ display: isCustomFilterSurveys ? 'block' : 'none' }}>
      <Table
        rowKey="surveyId"
        columns={columns}
        dataSource={dataSource}
        pagination={false}
        bordered={false}
        scroll={{ x: undefined, y: undefined }}
      />
    </div>
  )
}

const Table = styled(UnstyledTable)`
  .ant-table-thead > tr > th {
    background: white;
    border-bottom: 0;
  }

  .ant-table-tbody > tr > td {
    border-bottom: 0;
  }

  .ant-table-tbody > tr > td:first-child {
    font-size: 16px;
    z-index: 3;
  }

  .ant-table-tbody > tr.ant-table-row:hover > td {
    background: white;
  }
`

type SentimentColumnTitleProps = {
  sentiment: 'positive' | 'neutral' | 'negative'
  hasComment: boolean
}
function SentimentColumnTitle({ sentiment, hasComment }: SentimentColumnTitleProps) {
  if (sentiment === 'positive') {
    const actionText = hasComment ? 'with' : 'without'
    return (
      <div className="text-center">
        <i className="text-green text-base far fa-face-smile" />
        <p className="text-gray-dark italic">{actionText} comment</p>
      </div>
    )
  }

  if (sentiment === 'neutral') {
    const actionText = hasComment ? 'with' : 'without'
    return (
      <div className="text-center">
        <i className="text-orange text-base far fa-face-meh" />
        <p className="text-gray-dark italic">{actionText} comment</p>
      </div>
    )
  }

  if (sentiment === 'negative') {
    const actionText = hasComment ? 'with' : 'without'
    return (
      <div className="text-center">
        <i className="text-red text-base far fa-face-frown" />
        <p className="text-gray-dark italic">{actionText} comment</p>
      </div>
    )
  }

  return null
}



// WEBPACK FOOTER //
// ./src/components/SurveySentimentToggleSettings/index.tsx